import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AppLayout } from '@/components/AppLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Loader2, FileText, CheckCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface ProcessingState {
  vectorShiftResults: any;
  originalResume: string;
  jobTitle: string;
  jobDescription: string;
  generateAnalysis: boolean;
  selectedDocuments: string[];
}

interface DocmosisResumeData {
  header: {
    name: string;
    email: string;
    phone: string;
    location: string;
    linkedin: string;
    website: string;
  };
  summary: string;
  experience: Array<{
    title: string;
    company: string;
    location: string;
    startDate: string;
    endDate: string;
    bullets: string[];
  }>;
  education: Array<{
    degree: string;
    school: string;
    location: string;
    graduationDate: string;
    gpa?: string;
  }>;
  skills: Array<{
    category: string;
    items: string[];
  }>;
}

const ResumeProcessing: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [processingStep, setProcessingStep] = useState(0);
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState('Initializing...');
  const [generatedFiles, setGeneratedFiles] = useState<string[]>([]);

  const state = location.state as ProcessingState;

  useEffect(() => {
    console.log('ResumeProcessing mounted with state:', state);
    
    if (!state?.vectorShiftResults) {
      console.error('No VectorShift results found in state');
      toast.error('No processing data found. Redirecting...');
      navigate('/resume-tailoring-fixed-duplicate');
      return;
    }

    // For now, skip the complex processing and directly create tabs with the results
    createDirectTabs();
  }, [state]);

  const createDirectTabs = async () => {
    try {
      console.log('Creating Word documents from VectorShift results');
      setProcessingStep(1);
      setProgress(25);
      setStatusMessage('Generating Word documents...');

      const { vectorShiftResults, selectedDocuments, jobTitle } = state;
      const fileUrls: string[] = [];

      // Prepare documents array for Docmosis
      const documents = selectedDocuments.map(docType => {
        let content = '';
        let title = '';
        let type = docType;

        switch (docType) {
          case 'resume':
            content = vectorShiftResults.Final_Resume_Text || '';
            title = `Tailored Resume - ${jobTitle || 'Position'}`;
            break;
          case 'cover-letter':
            content = vectorShiftResults.Cover_Letter || '';
            title = `Cover Letter - ${jobTitle || 'Position'}`;
            break;
          case 'thank-you-letter':
            content = vectorShiftResults.Thank_You_Letter || '';
            title = `Thank You Letter - ${jobTitle || 'Position'}`;
            break;
          case 'executive-bio':
            content = vectorShiftResults.Executive_Bio || '';
            title = `Executive Bio - ${jobTitle || 'Position'}`;
            break;
          case 'linkedin-profile':
            content = vectorShiftResults.Linkedin_Profile || '';
            title = `LinkedIn Profile - ${jobTitle || 'Position'}`;
            break;
          case 'outreach-letter':
            content = vectorShiftResults.Outreach_Letter || '';
            title = `Outreach Letter - ${jobTitle || 'Position'}`;
            break;
        }

        return {
          id: `doc_${Date.now()}_${Math.random()}`,
          type,
          title,
          content: content.trim(),
          jobTitle: state.jobTitle,
          company: state.jobDescription ? 'Target Company' : undefined
        };
      }).filter(doc => doc.content);

      setProgress(50);
      setStatusMessage('Processing documents with template...');

      // Generate Word documents using Docmosis
      const { data, error } = await supabase.functions.invoke('export-to-word', {
        body: {
          documents,
          templateName: 'professional'
        }
      });

      if (error) {
        console.error('Error generating Word documents:', error);
        throw error;
      }

      setProgress(75);
      setStatusMessage('Saving documents...');

      // Save each generated document
      for (let i = 0; i < documents.length; i++) {
        const doc = documents[i];
        const fileName = `${Date.now()}_${doc.title.replace(/[^a-zA-Z0-9]/g, '_')}.docx`;
        
        // The response should be a blob or base64 data
        let blob: Blob;
        if (data instanceof Blob) {
          blob = data;
        } else if (typeof data === 'string') {
          // If it's base64, convert to blob
          const binaryString = atob(data);
          const bytes = new Uint8Array(binaryString.length);
          for (let j = 0; j < binaryString.length; j++) {
            bytes[j] = binaryString.charCodeAt(j);
          }
          blob = new Blob([bytes], { 
            type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' 
          });
        } else {
          throw new Error('Invalid document data received');
        }

        // Upload to storage
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('word-documents')
          .upload(fileName, blob, {
            cacheControl: '3600',
            upsert: false
          });

        if (uploadError) {
          console.error('Upload error:', uploadError);
        } else {
          const { data: urlData } = supabase.storage
            .from('word-documents')
            .getPublicUrl(fileName);
          
          fileUrls.push(urlData.publicUrl);
          console.log('Generated Word document:', fileName);
        }
      }

      setProgress(100);
      setProcessingStep(5);
      setStatusMessage('Opening Word documents...');
      setGeneratedFiles(fileUrls);

      // Navigate to word document viewer with the files
      setTimeout(() => {
        navigate('/word-document-viewer', {
          state: {
            generatedFiles: fileUrls,
            documentType: 'tailored-resume',
            jobTitle: state.jobTitle,
            jobDescription: state.jobDescription
          }
        });
      }, 1000);

    } catch (error) {
      console.error('Error generating Word documents:', error);
      toast.error('Error generating Word documents. Please try again.');
      navigate('/resume-tailoring-fixed-duplicate');
    }
  };

  const processVectorShiftResults = async () => {
    try {
      const steps = [
        'Parsing VectorShift results...',
        'Formatting with OpenAI...',
        'Generating Word documents...',
        'Saving to storage...',
        'Opening in document viewer...'
      ];

      // Step 1: Parse VectorShift results
      setProcessingStep(1);
      setProgress(20);
      setStatusMessage(steps[0]);
      await new Promise(resolve => setTimeout(resolve, 1000));

      console.log('VectorShift Results:', state.vectorShiftResults);
      console.log('Selected Documents:', state.selectedDocuments);

      // Step 2: Format with OpenAI
      setProcessingStep(2);
      setProgress(40);
      setStatusMessage(steps[1]);
      
      const formattedData = await formatWithOpenAI(state.vectorShiftResults);
      
      // Step 3: Generate Word documents with Docmosis
      setProcessingStep(3);
      setProgress(60);
      setStatusMessage(steps[2]);
      
      const generatedFileUrls = await generateWordDocuments(formattedData);
      
      // Step 4: Save to storage
      setProcessingStep(4);
      setProgress(80);
      setStatusMessage(steps[3]);
      
      // Files are already saved in the generateWordDocuments step
      setGeneratedFiles(generatedFileUrls);
      
      // Step 5: Open in document viewer
      setProcessingStep(5);
      setProgress(100);
      setStatusMessage(steps[4]);
      
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Navigate to word document viewer with the generated files
      navigate('/word-document-viewer', {
        state: {
          generatedFiles: generatedFileUrls,
          documentType: 'tailored-resume',
          jobTitle: state.jobTitle,
          jobDescription: state.jobDescription
        }
      });

    } catch (error) {
      console.error('Error processing VectorShift results:', error);
      toast.error('Error processing results. Please try again.');
      navigate('/resume-tailoring-fixed-duplicate');
    }
  };

  const formatWithOpenAI = async (vectorShiftResults: any): Promise<DocmosisResumeData[]> => {
    try {
      console.log('Formatting VectorShift results with OpenAI...');
      
      // Call OpenAI to format the results
      const { data, error } = await supabase.functions.invoke('format-resume-data', {
        body: {
          vectorShiftResults,
          selectedDocuments: state.selectedDocuments,
          jobTitle: state.jobTitle,
          jobDescription: state.jobDescription
        }
      });

      if (error) throw error;

      console.log('OpenAI formatted data:', data);
      return data.formattedDocuments || [];

    } catch (error) {
      console.error('Error formatting with OpenAI:', error);
      throw error;
    }
  };

  const generateWordDocuments = async (formattedData: DocmosisResumeData[]): Promise<string[]> => {
    try {
      console.log('Generating Word documents with Docmosis...');
      
      const fileUrls: string[] = [];
      
      for (const docData of formattedData) {
        // Call the bulk resume service (reusing existing Docmosis logic)
        const { data, error } = await supabase.functions.invoke('generate-bulk-resumes', {
          body: {
            resumes: [docData] // Single resume in array format
          }
        });

        if (error) throw error;

        if (data.success && data.file_data) {
          // Save the generated file to tailored-resumes bucket
          const fileName = `${Date.now()}_${docData.header.name?.replace(/\s+/g, '_') || 'resume'}_${state.jobTitle?.replace(/\s+/g, '_') || 'tailored'}.docx`;
          
          // Convert base64 to blob
          const binaryString = atob(data.file_data);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          const blob = new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });

          // Upload to Supabase storage
          const { data: uploadData, error: uploadError } = await supabase.storage
            .from('word-documents')
            .upload(fileName, blob, {
              cacheControl: '3600',
              upsert: false
            });

          if (uploadError) throw uploadError;

          // Get public URL
          const { data: urlData } = supabase.storage
            .from('word-documents')
            .getPublicUrl(fileName);

          fileUrls.push(urlData.publicUrl);
          console.log('Generated and saved document:', fileName);
        }
      }

      return fileUrls;

    } catch (error) {
      console.error('Error generating Word documents:', error);
      throw error;
    }
  };

  const stepIcons = [
    <Loader2 className="h-5 w-5 animate-spin" />,
    <FileText className="h-5 w-5" />,
    <FileText className="h-5 w-5" />,
    <FileText className="h-5 w-5" />,
    <CheckCircle className="h-5 w-5 text-green-600" />
  ];

  return (
    <AppLayout>
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          <Card className="shadow-lg">
            <CardContent className="p-8">
              <div className="text-center mb-8">
                <h1 className="text-2xl font-bold text-foreground mb-2">
                  Processing Your Resume
                </h1>
                <p className="text-muted-foreground">
                  Generating professional Word documents from your tailored content...
                </p>
              </div>

              <div className="space-y-6">
                {/* Progress Bar */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Progress</span>
                    <span className="font-medium">{progress}%</span>
                  </div>
                  <Progress value={progress} className="h-2" />
                </div>

                {/* Current Step */}
                <div className="flex items-center gap-3 p-4 bg-muted/50 rounded-lg">
                  {stepIcons[processingStep] || stepIcons[0]}
                  <span className="font-medium">{statusMessage}</span>
                </div>

                {/* Processing Steps */}
                <div className="space-y-3">
                  {[
                    'Parse VectorShift results',
                    'Format with AI',
                    'Generate Word documents',
                    'Save to storage',
                    'Open document viewer'
                  ].map((step, index) => (
                    <div key={index} className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${
                      index < processingStep ? 'bg-green-50 text-green-800' :
                      index === processingStep ? 'bg-blue-50 text-blue-800' :
                      'bg-muted/30 text-muted-foreground'
                    }`}>
                      {index < processingStep ? (
                        <CheckCircle className="h-4 w-4 text-green-600" />
                      ) : index === processingStep ? (
                        <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                      ) : (
                        <div className="h-4 w-4 rounded-full border-2 border-muted-foreground/30" />
                      )}
                      <span className="text-sm font-medium">{step}</span>
                    </div>
                  ))}
                </div>

                {/* Selected Documents Info */}
                {state?.selectedDocuments && (
                  <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                    <h3 className="font-medium text-blue-900 mb-2">Generating Documents:</h3>
                    <div className="flex flex-wrap gap-2">
                      {state.selectedDocuments.map((doc, index) => (
                        <span key={index} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-md">
                          {doc.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </AppLayout>
  );
};

export default ResumeProcessing;